import validate from "validate.js";

import {
  LOCAL_AUSTRALIAN_NUMBER_REGEX,
  AUSTRALIAN_POSTCODE_REGEX,
  EMAIL_MAXIMUM_LENGTH,
  FIRST_NAME_REGEX,
  INVALID_AUSTRALIAN_NUMBER_ERROR_MESSAGE,
  INVALID_FIRST_NAME_ERROR_MESSAGE,
  INVALID_LAST_NAME_ERROR_MESSAGE,
  INVALID_POSTCODE_ERROR_MESSAGE,
  LAST_NAME_REGEX,
  NAME_MAXIMUM_LENGTH,
  PASSWORD_MAXIMUM_LENGTH,
  PASSWORD_MINIMUM_LENGTH,
  VERIFICATION_CODE_LENGTH,
  INTL_AUSTRALIAN_NUMBER_REGEX
} from "../constants/form";

const passwordConstraints = {
  presence: { allowEmpty: false, message: "^Password is required" },
  length: {
    minimum: PASSWORD_MINIMUM_LENGTH,
    maximum: PASSWORD_MAXIMUM_LENGTH,
    message: "^Password must be between 8 and 256 characters"
  }
};

export const isValueValidBasedOnValidatorMethod = (
  value: string,
  validator: (value: string) => string
) => {
  return !validator(value);
};

export const customAmountValidator = (amount: string): string => {
  const constraints = {
    customAmount: {
      numericality: {
        notValid: "^Please enter a number",
        greaterThanOrEqualTo: 1,
        notGreaterThanOrEqualTo: "^Please enter an amount of at least $1"
      }
    }
  };

  const validation = validate({ customAmount: amount }, constraints);

  if (!validation) {
    return undefined;
  }

  return validation.customAmount[0];
};

export const nzfSupportAmountValidator = (amount: string): string => {
  if (amount === "") return "";

  const constraints = {
    nzfSupportAmount: {
      numericality: {
        notValid: "^Please enter a number",
        greaterThanOrEqualTo: 0,
        notGreaterThanOrEqualTo: "^Please enter a non-negative number"
      }
    }
  };

  const validation = validate({ nzfSupportAmount: amount }, constraints);

  if (!validation) {
    return "";
  }

  return validation.nzfSupportAmount[0];
};

export const firstNameValidator = (firstName: string): string => {
  const constraints = {
    firstName: {
      length: {
        minimum: 1,
        tooShort: "^Please enter your first name",
        maximum: NAME_MAXIMUM_LENGTH,
        tooLong: "^The name you entered is too long"
      },
      format: {
        pattern: FIRST_NAME_REGEX,
        message: `^${INVALID_FIRST_NAME_ERROR_MESSAGE}`
      }
    }
  };

  const validation = validate({ firstName }, constraints);

  if (!validation) {
    return undefined;
  }

  return validation.firstName[0];
};

export const lastNameValidator = (lastName: string): string => {
  const constraints = {
    lastName: {
      length: {
        minimum: 1,
        tooShort: "^Please enter your last name",
        maximum: NAME_MAXIMUM_LENGTH,
        tooLong: "^The name you entered is too long"
      },
      format: {
        pattern: LAST_NAME_REGEX,
        message: `^${INVALID_LAST_NAME_ERROR_MESSAGE}`
      }
    }
  };

  const validation = validate({ lastName }, constraints);

  if (!validation) {
    return undefined;
  }

  return validation.lastName[0];
};

export const emailValidator = (email: string): string => {
  const constraints = {
    email: {
      email: {
        message: "^Please enter a valid email"
      },
      length: {
        minimum: 1,
        tooShort: "^Please enter your email address",
        maximum: EMAIL_MAXIMUM_LENGTH,
        tooLong: "^The email you entered is too long"
      }
    }
  };

  const validation = validate({ email }, constraints);

  if (!validation) {
    return undefined;
  }

  return validation.email[0];
};

export const passwordValidator = (password: string) => {
  const constraints = {
    password: passwordConstraints
  };

  const validationResult = validate({ password }, constraints);

  if (!validationResult) return "";

  return validationResult.password[0];
};

export const confirmPasswordValidator = (confirmPassword: string, password: string) => {
  const constraints = {
    password: passwordConstraints,
    confirmPassword: {
      presence: { allowEmpty: false, message: "^Please confirm your password" },
      equality: {
        attribute: "password",
        message: "^Passwords do not match"
      }
    }
  };

  const validationResult = validate({ password, confirmPassword }, constraints);

  if (!validationResult) return "";

  return validationResult.password?.[0] ?? validationResult.confirmPassword?.[0] ?? "";
};

export const mobileValidator = (mobile: string, isRequired = true): string => {
  if (!isRequired && !mobile) {
    return undefined;
  }

  const constraints = {
    mobile: {
      format: {
        pattern: LOCAL_AUSTRALIAN_NUMBER_REGEX,
        message: `^${INVALID_AUSTRALIAN_NUMBER_ERROR_MESSAGE}`
      },
      length: {
        minimum: 1,
        tooShort: "^Please enter your mobile number"
      }
    }
  };

  const validation = validate({ mobile }, constraints);

  if (!validation) {
    return undefined;
  }

  return validation.mobile[0];
};

export const postcodeValidator = (postcode: string, isRequired = true): string => {
  if (!isRequired && !postcode) {
    return undefined;
  }

  const constraints = {
    postcode: {
      format: {
        pattern: AUSTRALIAN_POSTCODE_REGEX,
        message: `^${INVALID_POSTCODE_ERROR_MESSAGE}`
      },
      length: {
        minimum: 1,
        tooShort: "^Please enter your postcode"
      }
    }
  };

  const validation = validate({ postcode }, constraints);

  if (!validation) {
    return undefined;
  }

  return validation.postcode[0];
};

export const verificationCodeValidator = (verificationCode: string) => {
  const constraints = {
    verificationCode: {
      presence: {
        allowEmpty: false,
        message: "^Verification code is required"
      },
      format: {
        pattern: new RegExp(`^\\d{${VERIFICATION_CODE_LENGTH}}$`),
        message: `^Your code should be ${VERIFICATION_CODE_LENGTH} digits`
      }
    }
  };

  const validationResult = validate({ verificationCode }, constraints);

  if (!validationResult) return "";

  return validationResult.verificationCode[0];
};

export const dayValidator = (day: string): string => {
  const constraints = {
    day: {
      presence: {
        allowEmpty: false,
        message: `^Please enter a day`
      },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo: 1,
        lessThanOrEqualTo: 31,
        notValid: `^Please enter a valid day`,
        notInteger: `^Please enter a valid day`,
        notGreaterThanOrEqualTo: `^Day must be between 1 and 31`,
        notLessThanOrEqualTo: `^Day must be between 1 and 31`
      }
    }
  };

  const validation = validate({ day }, constraints);

  if (!validation) {
    return undefined;
  }

  return validation.day[0];
};

export const sanitizeEmailAddress = (email: string): string => {
  return email.trim().toLowerCase();
};

export const generateFullNameFromNameParts = (firstName: string, lastName: string): string => {
  return `${firstName || ""} ${lastName || ""}`.trim();
};

export const generateSentVerificationCodeMessage = (email: string) => {
  return `We've sent a verification code to ${email}. Check your inbox and please enter it below.`;
};

export const generateResendCodeMessage = (email: string) => {
  return `A new code has been sent to ${email}. Check your inbox and please enter it below.`;
};

export const convertIntlMobileToLocal = (mobile: string | null): string | null => {
  if (!mobile) return null;

  const sanitizedMobile = mobile.trim();

  if (INTL_AUSTRALIAN_NUMBER_REGEX.test(sanitizedMobile)) {
    return "0" + sanitizedMobile.slice(3);
  }

  if (LOCAL_AUSTRALIAN_NUMBER_REGEX.test(sanitizedMobile)) {
    return mobile;
  }

  return null;
};

export const requiredSelectValidator = (value: string): string => {
  return !value ? "Please select an option" : "";
};

export const arePersonalDetailsFieldsEmpty = ({
  firstName = "",
  lastName = "",
  email = "",
  mobile = "",
  postcode = ""
}: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  mobile?: string | null;
  postcode?: string | null;
} = {}): boolean =>
  !firstName?.trim() && !lastName?.trim() && !email?.trim() && !mobile?.trim() && !postcode?.trim();
